import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ClientService } from '../../../global/services/client.service';
import { DropDownTypeEnum } from '../filter-dropdown/dropdown.component';
import { CountriesService } from '../../../global/services/countries.service';
import { HelperFunctions } from '../../../utils/helper.functions';
import { DropdownItem } from '../../../global/types/DropdownItem';
import { ShippingLocationsComponent } from './shipping-locations/shipping-locations.component';
import { ClientsTabComponent } from './clients-tab/clients-tab.component';
import { CargoTabComponent } from './cargo-tab/cargo-tab.component';
import { AddInnerLocationPopupComponent } from './add-inner-location/add-inner-location.component';
import { InnerLocation } from '../../../global/types/InnerLocation';
import { InnerLocationService } from '../../../global/services/innerLocation.service';
import { LocationsService } from '../../../global/services/locations.service';
import { ToastersService } from '../../../global/services/toasters.service';
import { Client } from '../../../global/types/Client';
import { PermissionService } from '../../../global/services/permission.service';

export type TemplateTabs = 'Cargo' | 'ClientLocations' | 'InnerLocations' | 'Clients';

@Component({
  selector: 'app-shared-templates',
  templateUrl: './shared-templates.component.html',
  styleUrls: ['./shared-templates.component.less'],
})
export class SharedTemplatesComponent implements OnInit, AfterViewInit {
  @ViewChild(ShippingLocationsComponent) shippingLocationsComponent: ShippingLocationsComponent;
  @ViewChild(ClientsTabComponent) cientsTabComponent: ClientsTabComponent;
  @ViewChild(CargoTabComponent) cargoTabComponent: CargoTabComponent;

  isLoading = false;

  dropDownTypeEnum = DropDownTypeEnum;
  activeTab: TemplateTabs = 'Cargo';

  countries: DropdownItem[] = [];
  selectedClient: Client[] = [];
  selectedCountry: DropdownItem;
  clients: Client[];
  clientSearch = '';

  data: any;
  hasPermissionToSeeClients = true;
  openPopup = false;

  currentFilter: { search?: string; clientIds?: string[]; countryIso3?: string } = {
    search: '',
    clientIds: [],
    countryIso3: '',
  };

  constructor(
    private clientService: ClientService,
    private countryService: CountriesService,
    private helpers: HelperFunctions,
    private modal: NgbModal,
    private locationService: LocationsService,
    private innerLocationService: InnerLocationService,
    private toastersService: ToastersService,
    private permissions: PermissionService,
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.hasPermissionToSeeClients = this.permissions.isSubCon();
    this.initDropdownItems();
    this.data = null;
  }

  async ngAfterViewInit() {
    await this.populateCountriesDropdown();
    this.isLoading = false;
  }

  initDropdownItems() {
    this.countries = [{ title: 'Country: All', value: '' }];
  }

  async populateCountriesDropdown() {
    const countries = await this.countryService.getCountries();
    this.countries.push(...this.helpers.populateDropdownList(countries, '', (item) => item.iso3));
  }

  onLocationSearch(event) {
    this.currentFilter = { ...this.currentFilter, search: event };
  }

  async searchClients() {
    this.isLoading = true;
    try {
      if (!this.clientSearch.trim()) {
        this.clients = [];
      } else {
        this.clients = await this.clientService.getClients({ name: this.clientSearch });
      }
    } catch (error) {
      this.toastersService.createErrorToast(error.message);
    } finally {
      this.isLoading = false;
    }
  }

  async filterLocationsByCountry(country) {
    this.selectedCountry = country;
    const countryIso3 = country.value;
    this.currentFilter = { ...this.currentFilter, countryIso3 };
  }

  async filterLocationsByClient(client: Client[]) {
    this.selectedClient = client;
    const clientId = client[0].id;
    this.currentFilter = { ...this.currentFilter, clientIds: [clientId] };
  }

  clearFilters() {
    this.selectedCountry = null;
    this.selectedClient = [];
    this.currentFilter = {
      search: '',
      clientIds: [],
      countryIso3: '',
    };
  }

  haveUsedFilters(): boolean {
    return Object.values(this.currentFilter).some((value) => !!value);
  }

  stopTrigger(event) {
    const data = event.data;
    const trigger = event.trigger;
    this[trigger](data);
  }

  openPopups() {
    if (this.activeTab !== 'InnerLocations') {
      this.data = null;
      this.openPopup = !this.openPopup;
    } else {
      this.addInnerLocation();
    }
  }

  async addInnerLocation(value?: InnerLocation, isEdit: boolean = true) {
    if (value) this.innerLocationService.updateInnerLocationData(value);
    const modal = this.modal.open(AddInnerLocationPopupComponent, {
      centered: true,
      backdropClass: 'popup-background',
      windowClass: 'popup-box',
    });
    const componentInstance = modal.componentInstance as AddInnerLocationPopupComponent;
    componentInstance.isEditingAvailable = isEdit;

    const result = await modal.result;
    if (!result) {
      return;
    }
    try {
      this.isLoading = true;
      isEdit
        ? await this.locationService.addInnerLocation(result)
        : await this.locationService.editInnerLocation(result);
      await this.shippingLocationsComponent.emptyListAndLoadFirstPage();
    } catch (error) {
      this.toastersService.createErrorToast(error?.message || 'Something went wrong. Please try again');
    } finally {
      this.isLoading = false;
    }
  }

  async closePopup(isAdded?: boolean) {
    if (isAdded) {
      if (this.activeTab === 'Cargo') {
        await this.cargoTabComponent.getLoads();
      }
      if (this.activeTab === 'ClientLocations' || this.activeTab === 'InnerLocations') {
        await this.shippingLocationsComponent.emptyListAndLoadFirstPage();
      }
    }
    this.openPopup = !this.openPopup;
    this.data = null;
  }

  editData(value) {
    if (this.activeTab !== 'InnerLocations') {
      this.data = value;
      this.openPopup = true;
    } else {
      this.addInnerLocation(value, true);
    }
  }
  openLocationDetails(value: InnerLocation) {
    this.addInnerLocation(value, false);
  }
}
